import type { ResultOf, VariablesOf } from "@opensea/graphql"
import { graphql, useQuery } from "@opensea/graphql"
import type { CombinedError, UseQueryExecute } from "urql"
import { ActionTimelineFragment } from "../../../../components/ActionTimeline/ActionTimeline.fragment"
import { NETWORK_ONLY_OPERATION_CONTEXT } from "../../../../constants"
import { useAccount } from "../../../../polychain/hooks/useAccount"
import type { Address } from "../../../../types"
import { useSetBlockchainActionErrors } from "../../../hooks/useSetBlockchainActionErrors"
import { useSetBlockchainActionErrorsFragment } from "../../../hooks/useSetBlockchainActionErrors.fragment"

const AcceptOfferQuery = graphql(
  `
    query AcceptOfferQuery(
      $address: Address!
      $itemQuantities: [ItemQuantityInput!]!
      $applyOptionalCreatorFees: Boolean
    ) {
      acceptOffers(
        address: $address
        itemQuantities: $itemQuantities
        applyOptionalCreatorFees: $applyOptionalCreatorFees
      ) {
        actions {
          ...ActionTimeline
        }
        errors {
          ...useSetBlockchainActionErrors
        }
      }
    }
  `,
  [ActionTimelineFragment, useSetBlockchainActionErrorsFragment],
)

type UseAcceptOfferQueryProps = Omit<
  VariablesOf<typeof AcceptOfferQuery>,
  "address"
> & {
  address?: string
  pause?: boolean
  applyOptionalCreatorFees?: boolean
}

type UseAcceptOfferQuery = {
  actions: ResultOf<typeof AcceptOfferQuery>["acceptOffers"]["actions"]
  error: CombinedError | undefined
  fetching: boolean
  refetch: UseQueryExecute
}

export const useAcceptOfferQuery = (
  params: UseAcceptOfferQueryProps,
): UseAcceptOfferQuery => {
  const { itemQuantities, pause, applyOptionalCreatorFees } = params
  const { address } = useAccount()

  const [{ data, error, fetching }, refetch] = useQuery({
    query: AcceptOfferQuery,
    variables: {
      address: address as NonNullable<Address>,
      itemQuantities,
      applyOptionalCreatorFees,
    },
    pause: !address || pause,
    context: NETWORK_ONLY_OPERATION_CONTEXT,
  })
  const actions = data?.acceptOffers.actions ?? []

  useSetBlockchainActionErrors(data?.acceptOffers.errors)

  return {
    actions,
    error,
    fetching,
    refetch: () => refetch({ requestPolicy: "network-only" }),
  }
}
